import { SKILL_IDS } from '../domain/skills.js';
import { normalizarHabilidadeId } from './skillAliases.js';
import { habilidadeLabels, todayISO } from './utils.js';

export const EVOLUTION_SKILLS = SKILL_IDS;
const STATUS_SCORE = { nao_desenvolvido: 25, em_desenvolvimento: 60, consolidado: 90 };
const MS_PER_DAY = 86400000;

export function numericScore(value) {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
  if (value && typeof value === 'object') return numericScore(value.score ?? value.valor ?? value.nota ?? value.percentual);
  const normalized = String(value).toLowerCase().trim();
  if (STATUS_SCORE[normalized] !== undefined) return STATUS_SCORE[normalized];
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : null;
}

export function normalizeAssessmentSnapshot(snapshot = {}) {
  const source = snapshot.pontuacoes || snapshot.habilidades || snapshot.scores || snapshot.resultados || {};
  const scores = {};
  for (const [rawId, value] of Object.entries(source)) {
    const id = normalizarHabilidadeId(rawId);
    const score = numericScore(value);
    if (score !== null) scores[id] = score;
  }
  return { id: snapshot.id, date: snapshot.data || snapshot.date || snapshot.createdAt || '', scores, source: snapshot.origem || snapshot.type || 'assessment', tipo: snapshot.tipo || null };
}

export function assessmentSeries(data = {}, studentId) {
  const sources = [...(data.assessments || []), ...(data.diagnoses || []), ...(data.checklists || [])];
  const unique = new Map();
  sources.filter((item) => item.studentId === studentId || item.alunoId === studentId).map(normalizeAssessmentSnapshot).filter((item) => Object.keys(item.scores).length).forEach((item) => unique.set(item.id || `${item.date}:${JSON.stringify(item.scores)}`, item));
  return [...unique.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export function logSeries(data = {}, studentId) {
  return (data.logs || []).filter((log) => log.studentId === studentId || log.alunoId === studentId).map((log) => {
    const items = Array.isArray(log.itens) ? log.itens : [];
    const scores = items.map((item) => {
      if (typeof item.score === 'number') return item.score <= 1 ? item.score * 100 : item.score;
      if (item.resultado === 'conseguiu') return 100;
      if (item.resultado === 'parcial') return 50;
      if (item.resultado === 'nao_conseguiu' || item.resultado === 'não conseguiu') return 0;
      return null;
    }).filter((value) => value !== null);
    return { date: log.data || log.date || '', score: scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null, skills: [...new Set(items.flatMap((item) => item.habilidades || []).map(normalizarHabilidadeId))], origin: log.origem || 'manual' };
  }).filter((item) => item.date).sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export function averageScore(scores = {}) {
  const values = Object.values(scores).filter(Number.isFinite);
  return values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
}

function daysBetween(from, to) {
  const start = new Date(from); const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

function skillVelocity(skill, assessments) {
  const points = assessments.map((item) => ({ date: item.date, score: item.scores[skill] })).filter((item) => item.date && Number.isFinite(item.score));
  if (points.length < 2) return null;
  const first = points[0]; const last = points.at(-1);
  const days = daysBetween(first.date, last.date);
  if (!days || days <= 0) return null;
  return (last.score - first.score) / days;
}

export function buildEvolutionSummary(data = {}, studentId) {
  const assessments = assessmentSeries(data, studentId);
  const logs = logSeries(data, studentId);
  const initialScores = {};
  const currentScores = {};

  for (const assessment of assessments) {
    for (const [skill, score] of Object.entries(assessment.scores)) {
      if (!Object.prototype.hasOwnProperty.call(initialScores, skill)) initialScores[skill] = score;
      currentScores[skill] = score;
    }
  }

  const firstSnapshot = assessments[0] || { date: '' };
  const lastSnapshot = assessments.at(-1) || firstSnapshot;
  const initial = { ...firstSnapshot, scores: initialScores };
  const current = { ...lastSnapshot, scores: currentScores };
  const observedSkills = Object.keys(currentScores);
  const skills = [
    ...EVOLUTION_SKILLS.filter((skill) => observedSkills.includes(skill)),
    ...observedSkills.filter((skill) => !EVOLUTION_SKILLS.includes(skill)),
  ];
  const comparison = skills.map((skill) => {
    const before = initialScores[skill];
    const now = currentScores[skill];
    return { skill, before, now, delta: now - before, velocidadeDiaria: skillVelocity(skill, assessments) };
  });
  const improved = [...comparison].filter((item) => item.delta > 0).sort((a, b) => b.delta - a.delta);
  const declined = [...comparison].filter((item) => item.delta < 0).sort((a, b) => a.delta - b.delta);
  const priorities = [...comparison].sort((a, b) => a.now - b.now || a.delta - b.delta).slice(0, 5);
  const initialAverage = averageScore(initialScores);
  const currentAverage = averageScore(currentScores);
  return { assessments, logs, initial, current, comparison, improved, declined, priorities, initialAverage, currentAverage, deltaAverage: currentAverage - initialAverage, copilotSessions: logs.filter((log) => ['copiloto_professor_v17', 'copiloto_professor_v18'].includes(log.origin)).length };
}

const ALERT_SEVERITY_ORDER = { alta: 0, media: 1, baixa: 2 };

/**
 * Lê o resumo de evolução (buildEvolutionSummary) e traduz padrões em alertas
 * pedagógicos acionáveis: regressão, estagnação, ausência de atendimento recente
 * e habilidades consolidadas dignas de nota.
 */
export function buildPerformanceAlerts(summary, { today = todayISO(), stagnationThreshold = 3, staleDays = 30 } = {}) {
  if (!summary || !summary.assessments?.length) return [];
  const alerts = [];
  for (const item of summary.declined.slice(0, 3)) {
    if (item.delta <= -10) alerts.push({ tipo: 'regressao', severidade: 'alta', habilidade: item.skill, mensagem: `${habilidadeLabels[item.skill] || item.skill} recuou ${Math.abs(item.delta)} pontos desde a primeira avaliação.` });
  }
  if (summary.assessments.length >= 2 && Math.abs(summary.deltaAverage) < stagnationThreshold) {
    alerts.push({ tipo: 'estagnacao_geral', severidade: 'media', habilidade: null, mensagem: `Média global variou apenas ${summary.deltaAverage} ponto(s) entre ${summary.initial.date} e ${summary.current.date}. Considere ajustar a atividade indicada.` });
  }
  const lastLogDate = summary.logs.at(-1)?.date;
  const semAtendimento = lastLogDate ? daysBetween(lastLogDate, today) : null;
  if (!summary.logs.length) {
    alerts.push({ tipo: 'sem_atendimento', severidade: 'media', habilidade: null, mensagem: 'Nenhum atendimento registrado ainda para este estudante.' });
  } else if (Number.isFinite(semAtendimento) && semAtendimento > staleDays) {
    alerts.push({ tipo: 'sem_atendimento_recente', severidade: 'media', habilidade: null, mensagem: `${semAtendimento} dias desde o último atendimento registrado.` });
  }
  const consolidada = summary.comparison.find((item) => item.now >= 90 && item.delta > 0);
  if (consolidada) alerts.push({ tipo: 'habilidade_consolidada', severidade: 'baixa', habilidade: consolidada.skill, mensagem: `${habilidadeLabels[consolidada.skill] || consolidada.skill} atingiu consolidação (${consolidada.now}/100).` });
  return alerts.sort((a, b) => ALERT_SEVERITY_ORDER[a.severidade] - ALERT_SEVERITY_ORDER[b.severidade]);
}

function metaSkillIds(meta = {}) {
  const lista = meta.habilidades_alvo || meta.habilidades || meta.habilidade_alvo || meta.habilidade;
  return (Array.isArray(lista) ? lista : [lista]).filter(Boolean).map(normalizarHabilidadeId);
}

/**
 * Projeta se uma meta do PEI/PAEE tende a ser cumprida até o prazo, a partir da
 * velocidade de evolução observada nas habilidades vinculadas à meta.
 */
export function projectGoalCompletion(meta = {}, summary, { today = todayISO() } = {}) {
  const skills = metaSkillIds(meta);
  const alvo = Number(meta.alvo_numerico ?? 100);
  const linhaBase = Number(meta.linha_base ?? summary?.initialAverage ?? 0);
  if (!skills.length || !summary?.comparison?.length) return { status: 'sem_dados', alvo, atual: null, diasRestantes: null };
  const relevantes = summary.comparison.filter((item) => skills.includes(item.skill));
  if (!relevantes.length) return { status: 'sem_dados', alvo, atual: null, diasRestantes: null };
  const atual = Math.round(relevantes.reduce((sum, item) => sum + item.now, 0) / relevantes.length);
  const velocidades = relevantes.map((item) => item.velocidadeDiaria).filter(Number.isFinite);
  const velocidadeDiaria = velocidades.length ? velocidades.reduce((a, b) => a + b, 0) / velocidades.length : null;
  const diasRestantes = meta.prazo ? daysBetween(today, meta.prazo) : null;
  const pontosRestantes = alvo - atual;
  if (pontosRestantes <= 0) return { status: 'atingida', alvo, atual, diasRestantes, velocidadeDiaria, diasEstimados: 0 };
  if (velocidadeDiaria === null) return { status: 'sem_dados', alvo, atual, diasRestantes, velocidadeDiaria: null, diasEstimados: null };
  if (velocidadeDiaria <= 0) return { status: 'sem_progresso', alvo, atual, diasRestantes, velocidadeDiaria, diasEstimados: null };
  const diasEstimados = Math.ceil(pontosRestantes / velocidadeDiaria);
  if (diasRestantes === null) return { status: 'progredindo', alvo, atual, diasRestantes: null, velocidadeDiaria, diasEstimados };
  return { status: diasEstimados <= diasRestantes ? 'no_prazo' : 'risco_atraso', alvo, atual, diasRestantes, velocidadeDiaria, diasEstimados, linhaBase };
}

export function buildGoalsProjection(metas = [], summary, options = {}) {
  return (metas || []).map((meta) => ({ meta, projecao: projectGoalCompletion(meta, summary, options) }));
}
